import MeetingRoomListItem from "./MeetingRoomListItem";
import MeetingRoomListSkeletonItem from "./MeetingRoomListSkeletonItem";

import { useEffect, useMemo } from "react";
import {
  MeetingRoomListItemWrapper,
  MeetingRoomListUL,
  MeetingRoomStatusLegendBox,
  MeetingRoomStatusLegendRow,
  MeetingRoomStatusLegendText,
  MeetingRoomStatusLegendWrapper,
} from "./MeetingRoomListStyled";
import {
  MeetingRoomListHeader,
  MeetingRoomListWrapper,
} from "./MeetingRoomListItemStyled";
import { Box, Tab, Tabs } from "@mui/material";
import {
  Brand200,
  Brand400,
  Brand600,
  Gray200,
  Green200,
} from "../../../styles/color";
import { useMeetingRoom } from "../../../hooks/MeetingRoom/useMeetingRoom";
import {
  DateInfo,
  MeetingRoomEventListInfo,
  MeetingRoomInfo,
  MeetingRoomLocation,
  MeetingRoomStatusInfo,
  OutlookDeepLinkParam,
} from "../../../system/types/meetingRoom/MeetingRoom";
import dayjs from "dayjs";
import { useDeviceType } from "../../../hooks/useDeviceType";

type MeetingRoomListProps = {
  isLoading: boolean;
  dateInfo: DateInfo;
  meetingRoomInfoList: MeetingRoomInfo[];
  selectedLocation: MeetingRoomLocation;
  handleSelectedLocation: (value: MeetingRoomLocation) => void;
  meetingRoomEventList: MeetingRoomEventListInfo["todayEventList"];
  selectedMeetingRoom: MeetingRoomStatusInfo | null;
  handleChangeSelectedMeetingRoom: (value: MeetingRoomStatusInfo) => void;
  handleOpenOutlookPopup: (params: OutlookDeepLinkParam[]) => void;
  handleOpenGuidePopup: (
    status: boolean,
    params: OutlookDeepLinkParam[]
  ) => void;
};

const MeetingRoomList = ({
  isLoading,
  selectedLocation,
  handleSelectedLocation,
  meetingRoomInfoList,
  meetingRoomEventList,
  handleChangeSelectedMeetingRoom,
  selectedMeetingRoom,
  handleOpenOutlookPopup,
  handleOpenGuidePopup,
}: MeetingRoomListProps) => {
  const {
    findMeetingRoomInfo,
    extractFloorInfo,
    getIsAvailable,
    sliceMeetingRoomName,
  } = useMeetingRoom();

  const meetingRoomStatusList = useMemo(() => {
    const result: MeetingRoomStatusInfo[] = Object.keys(meetingRoomEventList)
      .filter((name) =>
        meetingRoomInfoList.some((info) => info.displayName === name)
      )
      .map((name) => {
        const roomInfo = findMeetingRoomInfo(meetingRoomInfoList, name);
        return {
          id: name,
          name: sliceMeetingRoomName(name),
          roomInfo,
          isAvailable: getIsAvailable(
            meetingRoomEventList[name],
            dayjs().format("YYYY-MM-DDTHH:mm")
          ),
          floorInfo: extractFloorInfo(name),
        };
      })
      .sort((a, b) => b.floorInfo.floor - a.floorInfo.floor);

    return result ?? [];
  }, [
    meetingRoomEventList,
    meetingRoomInfoList,
    findMeetingRoomInfo,
    getIsAvailable,
    extractFloorInfo,
    sliceMeetingRoomName,
  ]);

  const placeList = useMemo(
    () => [
      { id: MeetingRoomLocation.IHQ, name: "본사" },
      { id: MeetingRoomLocation.FACTORY, name: "공장" },
      { id: MeetingRoomLocation.VENTURE, name: "벤처" },
    ],
    []
  );
  const deviceType = useDeviceType();

  useEffect(() => {
    if (selectedMeetingRoom === null) {
      const statusList = Object.keys(meetingRoomEventList)
        .filter((name) =>
          meetingRoomInfoList.some((info) => info.displayName === name)
        )
        .map((name) => {
          const roomInfo = findMeetingRoomInfo(meetingRoomInfoList, name);
          return {
            id: name,
            name: sliceMeetingRoomName(name),
            roomInfo,
            isAvailable: getIsAvailable(
              meetingRoomEventList[name],
              dayjs().format("YYYY-MM-DDTHH:mm")
            ),
            floorInfo: extractFloorInfo(name),
          };
        })
        .sort((a, b) => b.floorInfo.floor - a.floorInfo.floor);

      if (statusList.length > 0) {
        const middleIndex = Math.max(Math.floor(statusList.length / 2) - 1, 0);
        handleChangeSelectedMeetingRoom(statusList[middleIndex]);
      }
    }
  }, [
    meetingRoomEventList,
    meetingRoomInfoList,
    selectedMeetingRoom,
    handleChangeSelectedMeetingRoom,
    findMeetingRoomInfo,
    sliceMeetingRoomName,
    getIsAvailable,
    extractFloorInfo,
  ]);

  return (
    <MeetingRoomListWrapper deviceType={deviceType}>
      <MeetingRoomListHeader>
        <MeetingRoomStatusLegendWrapper>
          <MeetingRoomStatusLegendRow>
            <MeetingRoomStatusLegendBox
              sx={{
                background: Green200,
              }}
            />
            <MeetingRoomStatusLegendText>예약 가능</MeetingRoomStatusLegendText>
            <MeetingRoomStatusLegendBox
              sx={{
                background: Brand200,
              }}
            />
            <MeetingRoomStatusLegendText>예약 중</MeetingRoomStatusLegendText>
          </MeetingRoomStatusLegendRow>
        </MeetingRoomStatusLegendWrapper>
        <Box
          sx={{
            width: "100%",
            fontSize: "0.625rem",
            color: Brand400, // 여기만 교체
            textAlign: "center",
          }}
        >
          회의실 현황은 현재시간 기준으로 표시됩니다.
        </Box>
      </MeetingRoomListHeader>
      <Box
        sx={{
          display: "flex",
          flexDirection: deviceType === "mobile" ? "row" : "column",
          height: "100%",
          gap: 1,
        }}
      >
        <Tabs
          value={selectedLocation}
          onChange={(e, v) => handleSelectedLocation(v)}
          variant={"fullWidth"}
          orientation={deviceType === "mobile" ? "vertical" : "horizontal"}
          aria-label="Place Tabs"
          sx={{
            display: "flex",
            width: deviceType === "mobile" ? "60px" : "100%",
            padding: "0",
            justifyContent: "center",
            minHeight: "28px",
            marginTop: deviceType === "mobile" ? 0 : "8px",
            marginBottom: deviceType === "mobile" ? 0 : "16px",
            borderTop: `1px solid ${Gray200}`,
            color: Brand600,
            borderBottom: `1px solid ${Gray200}`,
          }}
        >
          {placeList.map((place) => (
            <Tab
              key={place.id}
              value={place.id}
              label={place.name}
              wrapped
              sx={{
                minWidth: "60px",
                minHeight: "28px",
                height: "32px",
                padding: "4px",
                backgroundColor: `${
                  selectedLocation === place.id ? Brand600 : "inherit"
                } !important`,
                fontWeight: selectedLocation === place.id ? 600 : 500,
              }}
            />
          ))}
        </Tabs>

        <MeetingRoomListUL deviceType={deviceType}>
          {meetingRoomStatusList.length === 0 || isLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <MeetingRoomListSkeletonItem key={index} />
              ))}
            </Box>
          ) : (
            meetingRoomStatusList.map((room, rIdx) => (
              <MeetingRoomListItemWrapper
                key={`Room_${room.id}_${rIdx}`}
                deviceType={deviceType}
              >
                <MeetingRoomListItem
                  handleOpenGuidePopup={handleOpenGuidePopup}
                  handleOpenOutlookPopup={handleOpenOutlookPopup}
                  selectedId={selectedMeetingRoom?.id ?? ""}
                  info={room}
                  idx={rIdx}
                  onClick={() => handleChangeSelectedMeetingRoom(room)}
                />
              </MeetingRoomListItemWrapper>
            ))
          )}
        </MeetingRoomListUL>
      </Box>
    </MeetingRoomListWrapper>
  );
};
export default MeetingRoomList;
